import { Injectable } from '@angular/core';
import { sync, reload, setConfig, resetConfig, getConfig, SyncResult, LiveUpdateConfig } from '@capacitor/live-updates';
import { Capacitor } from '@capacitor/core';
import { Subject } from 'rxjs';

export interface LiveUpdateEvent {
  type: 'update-downloaded' | 'reloading';
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LiveUpdatesService {
  private isInternalTeam = false;
  private appId: string = '64fc70e1'; // From capacitor.config.ts
  private channel: string = 'Production'; // From capacitor.config.ts
  private updateEventSubject = new Subject<LiveUpdateEvent>();
  public updateEvents$ = this.updateEventSubject.asObservable();

  /**
   * Configure Live Updates with Appflow app ID and channel
   * Enables Live Updates for internal team members
   */
  async configure(appId?: string, channel?: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Use provided values or fall back to defaults from config
    const finalAppId = appId || this.appId;
    const finalChannel = channel || this.channel;

    if (!finalAppId) {
      return;
    }

    this.appId = finalAppId;
    this.channel = finalChannel;

    try {
      const config: LiveUpdateConfig = {
        appId: finalAppId,
        channel: finalChannel,
        autoUpdateMethod: 'none', // Manual control - we sync via service
        strategy: 'differential',
        enabled: true,
        maxVersions: 2
      };

      await setConfig(config);
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Disable Live Updates (for non-internal team members)
   */
  async disable(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await resetConfig();
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Get current Live Updates configuration (for debugging/verification)
   */
  async getCurrentConfig(): Promise<LiveUpdateConfig | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      return await getConfig();
    } catch (error) {
      return null;
    }
  }

  /**
   * Download updates (does not reload - use syncAndReload() for auto-reload)
   */
  async sync(): Promise<SyncResult | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    if (!this.isInternalTeam) {
      return null;
    }

    // Ensure config is set before syncing
    if (!this.appId) {
      return null;
    }

    try {
      return await sync();
    } catch (error) {
      return null;
    }
  }

  /**
   * Apply downloaded update
   */
  async reload(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await reload();
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Sync and automatically reload if update is available
   */
  async syncAndReload(): Promise<SyncResult | null> {
    // Silent sync - no alerts during check/download
    const result = await this.sync();
    
    // ONLY emit events if an update was actually found and downloaded
    if (result && result.activeApplicationPathChanged) {
      // Emit event that update was downloaded - show alert to user
      this.updateEventSubject.next({
        type: 'update-downloaded',
        message: 'App update downloaded. The app will reload in a few seconds...'
      });
      
      // Give user time to see the alert (5 seconds) before reloading
      setTimeout(() => {
        // Emit event that we're about to reload
        this.updateEventSubject.next({
          type: 'reloading',
          message: 'Reloading app with new update...'
        });
        
        // Small additional delay to show "reloading" message
        setTimeout(() => {
          this.reload();
        }, 500);
      }, 5000);
    }
    
    return result;
  }

  /**
   * Call this after auth to set internal team status and optionally sync
   * @param isInternal - Whether user is internal team member
   * @param syncOnAuth - If true, automatically sync after setting status
   * @param appId - Appflow app ID (optional, will use from capacitor.config.ts if not provided)
   * @param channel - Channel name (optional, defaults to 'Production' from config)
   */
  async setInternalTeamStatus(isInternal: boolean, syncOnAuth: boolean = true, appId?: string, channel?: string) {
    this.isInternalTeam = isInternal;
    
    if (isInternal) {
      // Enable and configure Live Updates for internal team
      await this.configure(appId, channel);
      
      if (syncOnAuth) {
        // Sync and reload in background after a short delay to not block auth flow
        setTimeout(() => {
          this.syncAndReload();
        }, 2000);
      }
    } else {
      // Disable Live Updates for non-internal team members
      await this.disable();
    }
  }
}
